import { onDocumentReady } from "@theme/utilities";
import { ensureGsap, prefersReducedMotion as prefersReducedMotion$1 } from "@theme/vp-motion";
import { StandardEvents } from "@shopify/events";
import { buildRecommendationsUrl, cartProductIds, filterUpsellProducts, pickSeedProductId, recommendationImageUrl } from "@theme/vp-cart-upsells";
import { resolveCartProgressStage } from "@theme/vp-cart-progress";
import { GWP_PROPERTY_KEY, GWP_PROPERTY_VALUE, isGwpLine, qualifyingSubtotal, resolveGwpAction } from "@theme/vp-cart-gwp";
//#region assets/vp-header-flyouts.ts
/**
* Search and cart flyouts for the Poelgeest header.
*
* Motion mirrors the Figma prototype: the search panel drops in from -8px, the
* cart drawer slides in from +24px. GSAP writes inline styles, which override
* the CSS fallback used when GSAP is unavailable.
*/
var SEARCH_HISTORY_KEY = "vp:search-history";
/** @type {typeof window.gsap | null} */
var gsap = null;
/** @param {number} value */
var reduced = (value) => prefersReducedMotion$1() ? 0 : value;
/**
* @param {HTMLElement | null} element
* @param {boolean} open
*/
function setHidden(element, open) {
	if (element) element.hidden = !open;
}
/**
* Locks page scroll while a modal flyout is open.
* Compensates for the scrollbar gutter so content doesn't jump sideways.
* @param {boolean} locked
*/
function lockScroll$1(locked) {
	if (locked) {
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.overflow = "hidden";
		document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
		return;
	}
	document.body.style.overflow = "";
	document.body.style.paddingRight = "";
}
/** @param {string} value */
function escapeHtml(value) {
	const node = document.createElement("div");
	node.textContent = value;
	return node.innerHTML;
}
/** @returns {boolean} */
function isMobileViewport() {
	return window.matchMedia("(max-width: 749px)").matches;
}
var HeaderFlyouts = class {
	/** @type {'search' | 'cart' | null} */
	#active = null;
	/** @type {AbortController | null} */
	#cartRequest = null;
	/** @type {Map<number, number>} */
	#variantCompareAtPrices = /* @__PURE__ */ new Map();
	/** @type {AbortController | null} */
	#suggestRequest = null;
	/** @type {ReturnType<typeof setTimeout> | null} */
	#suggestTimer = null;
	/** @type {AbortController | null} */
	#upsellRequest = null;
	/** @type {Map<string, any[]>} */
	#upsellCache = /* @__PURE__ */ new Map();
	/** @type {boolean} */
	#gwpSyncing = false;
	constructor() {
		this.backdrop = document.querySelector("[data-vp-flyout-backdrop]");
		this.searchPanel = document.querySelector("[data-vp-search-panel]");
		this.searchToggle = document.querySelector("[data-vp-search-toggle]");
		this.searchForm = document.querySelector("[data-vp-search-form]");
		this.searchInput = document.querySelector("[data-vp-search-input]");
		this.searchClear = document.querySelector("[data-vp-search-clear]");
		this.searchList = document.querySelector("[data-vp-search-suggestions]");
		this.searchListTitle = document.querySelector("[data-vp-search-suggestions-title]");
		this.searchHistoryClear = document.querySelector("[data-vp-search-history-clear]");
		const panelData = this.searchPanel?.dataset ?? {};
		this.searchSuggestUrl = panelData.vpSearchSuggestUrl || "/search/suggest";
		this.searchUrl = panelData.vpSearchUrl || "/search";
		this.searchHistoryLimit = Number.parseInt(panelData.vpSearchHistoryLimit || "6", 10) || 6;
		this.searchHistoryTitle = panelData.vpSearchHistoryTitle || "Recent gezocht";
		this.searchFilteredTitle = panelData.vpSearchFilteredTitle || "Suggesties";
		this.searchHistoryEmptyText = panelData.vpSearchHistoryEmpty || "Je recente zoekopdrachten verschijnen hier.";
		this.searchEmptyText = panelData.vpSearchSuggestionsEmpty || "Geen suggesties gevonden.";
		/** @param {string} name */
		const iconHtml = (name) => (this.searchPanel?.querySelector(`template[data-vp-search-icon-${name}]`))?.innerHTML.trim() || "";
		this.searchIconHtml = iconHtml("suggestion");
		this.historyIconHtml = iconHtml("history");
		this.removeIconHtml = iconHtml("remove");
		this.cartPanel = document.querySelector("[data-vp-cart-panel]");
		this.cartToggles = Array.from(document.querySelectorAll("[data-vp-cart-toggle]"));
		this.#bindSearch();
		this.#bindCart();
		this.#bindGlobal();
		this.#syncGwpSilently();
	}
	get isOpen() {
		return this.#active !== null;
	}
	#bindGlobal() {
		this.backdrop?.addEventListener("click", () => this.closeAll());
		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && this.isOpen) this.closeAll();
		});
		document.addEventListener("theme-drawer:open", this.#onHorizonCartDrawerOpen);
		window.addEventListener("resize", this.#onViewportResize, { passive: true });
	}
	/** @type {() => void} */
	#onViewportResize = () => {
		if (isMobileViewport() || this.isOpen) return;
		lockScroll$1(false);
	};
	/**
	* Instantly dismiss Horizon's cart dialog (no close animation / top-layer flash).
	*/
	#forceCloseHorizonCart() {
		const drawer = document.querySelector("theme-drawer#cart-drawer");
		if (drawer instanceof HTMLElement) {
			const dialog = drawer.querySelector("dialog");
			drawer.removeAttribute("open");
			drawer.style.removeProperty("--drawer-stack-order");
			if (dialog instanceof HTMLDialogElement) {
				dialog.classList.remove("theme-drawer__dialog--opening", "theme-drawer__dialog--opening-inline-start", "theme-drawer__dialog--closing", "theme-drawer__dialog-instant");
				if (dialog.open) dialog.close();
			}
			document.querySelector(".page-wrapper")?.classList.remove("page-wrapper--drawer-open");
		}
		try {
			window.SLIDECART_CLOSE?.();
		} catch {}
		document.querySelector("#slidecarthq .slidecarthq.open")?.classList.remove("open");
		document.querySelector("#slidecarthq .slidecarthq-overlay.open")?.classList.remove("open");
	}
	/**
	* @param {Event} event
	*/
	#onHorizonCartDrawerOpen = (event) => {
		const drawer = event.target;
		if (!(drawer instanceof HTMLElement) || drawer.id !== "cart-drawer") return;
		if (!this.cartPanel) return;
		this.#forceCloseHorizonCart();
		this.openCart();
	};
	/** @param {MouseEvent} event */
	#onSearchOutsideClick = (event) => {
		if (this.#active !== "search") return;
		const target = event.target;
		if (!(target instanceof Element)) return;
		if (this.searchPanel?.contains(target)) return;
		if (this.searchToggle?.contains(target)) return;
		this.#closeSearch();
		this.#active = null;
		document.removeEventListener("click", this.#onSearchOutsideClick);
	};
	#bindSearchOutsideClick() {
		requestAnimationFrame(() => {
			document.addEventListener("click", this.#onSearchOutsideClick);
		});
	}
	#unbindSearchOutsideClick() {
		document.removeEventListener("click", this.#onSearchOutsideClick);
	}
	#bindSearch() {
		this.searchToggle?.addEventListener("click", () => {
			if (this.#active === "search") this.closeAll();
			else this.openSearch();
		});
		this.searchClear?.addEventListener("click", () => {
			if (!this.searchInput) return;
			this.searchInput.value = "";
			this.searchInput.focus();
			this.#updateSuggestions();
		});
		this.searchInput?.addEventListener("input", () => this.#updateSuggestions());
		this.searchForm?.addEventListener("submit", () => {
			this.#recordSearch(this.searchInput?.value || "");
		});
		this.searchHistoryClear?.addEventListener("click", () => {
			this.#writeHistory([]);
			this.#updateSuggestions();
			this.searchInput?.focus();
		});
		this.searchList?.addEventListener("click", (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			const remove = target.closest("[data-vp-search-history-remove]");
			if (remove instanceof HTMLElement) {
				event.preventDefault();
				const term = remove.dataset.vpSearchHistoryRemove || "";
				this.#writeHistory(this.#readHistory().filter((entry) => entry !== term));
				this.#renderHistory();
				return;
			}
			const row = target.closest("[data-vp-search-term]");
			if (row instanceof HTMLElement) this.#recordSearch(row.dataset.vpSearchTerm || "");
		});
	}
	/** @returns {string[]} */
	#readHistory() {
		try {
			const raw = window.localStorage.getItem(SEARCH_HISTORY_KEY);
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === "string") : [];
		} catch {
			return [];
		}
	}
	/** @param {string[]} entries */
	#writeHistory(entries) {
		try {
			window.localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(entries.slice(0, this.searchHistoryLimit)));
		} catch {}
	}
	/**
	* Moves a term to the front of the history, de-duplicating case-insensitively
	* so "BMW" typed twice doesn't take two slots.
	* @param {string} term
	*/
	#recordSearch(term) {
		const trimmed = term.trim();
		if (!trimmed) return;
		const key = trimmed.toLowerCase();
		const rest = this.#readHistory().filter((entry) => entry.toLowerCase() !== key);
		this.#writeHistory([trimmed, ...rest]);
	}
	#bindCart() {
		for (const toggle of this.cartToggles) toggle.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (this.#active === "cart") this.closeAll();
			else this.openCart();
		}, true);
		document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
		document.addEventListener("submit", this.#onProductCardSubmit, true);
		this.cartPanel?.addEventListener("submit", (event) => {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) return;
			if (!form.querySelector("[name=\"checkout\"]")) return;
			const countText = this.cartPanel?.querySelector("[data-vp-cart-count]")?.textContent || "0";
			const itemCount = Number.parseInt(countText, 10) || 0;
			if (itemCount === 0 || !this.#termsAccepted()) {
				event.preventDefault();
				this.#syncCheckoutButton(itemCount);
			}
		});
		this.cartPanel?.addEventListener("change", (event) => {
			const target = event.target;
			if (!(target instanceof HTMLInputElement)) return;
			if (!target.matches("[data-vp-cart-terms]")) return;
			this.#syncCheckoutButton();
		});
		this.cartPanel?.addEventListener("click", (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			const viewCart = target.closest("[data-vp-cart-view]");
			if (viewCart instanceof HTMLAnchorElement) {
				event.preventDefault();
				event.stopImmediatePropagation();
				const href = viewCart.getAttribute("href") || window.Theme?.routes?.cart_url || "/cart";
				window.location.assign(href);
				return;
			}
			if (target.closest("[data-vp-cart-close]")) {
				this.closeAll();
				return;
			}
			const upsellAdd = target.closest("[data-vp-cart-upsell-add]");
			if (upsellAdd instanceof HTMLElement) {
				event.preventDefault();
				const variantId = Number.parseInt(upsellAdd.dataset.vpCartUpsellAdd || "0", 10);
				if (variantId > 0) this.#addUpsellVariant(variantId, upsellAdd);
				return;
			}
			const item = target.closest("[data-vp-cart-item]");
			if (!(item instanceof HTMLElement)) return;
			const key = item.dataset.vpCartKey;
			if (!key) return;
			if (target.closest("[data-vp-cart-remove]")) {
				if (item.hasAttribute("data-vp-cart-gwp")) return;
				const removeBtn = target.closest("[data-vp-cart-remove]");
				this.#changeLine(key, 0, removeBtn instanceof HTMLElement ? removeBtn : item);
				return;
			}
			const stepper = target.closest("[data-vp-cart-qty]");
			if (!(stepper instanceof HTMLElement)) return;
			if (item.hasAttribute("data-vp-cart-gwp")) return;
			const delta = Number.parseInt(stepper.dataset.vpCartQty || "0", 10);
			const current = Number.parseInt(item.querySelector("[data-vp-cart-qty-value]")?.textContent?.trim() || "0", 10);
			this.#changeLine(key, Math.max(0, current + delta), stepper);
		}, true);
	}
	/**
	* @param {import('@shopify/events').CartLinesUpdateEvent} event
	*/
	#onCartLinesUpdate = (event) => {
		if (event.action !== "add") return;
		event.promise?.then((result) => {
			if (result?.detail?.didError) return;
			if (window.vpDirectCheckoutPending) return;
			this.openCart();
		}).catch(() => {});
	};
	/**
	* @param {SubmitEvent} event
	*/
	#onProductCardSubmit = (event) => {
		const form = event.target;
		if (!(form instanceof HTMLFormElement)) return;
		if (!form.classList.contains("vp-product-card__cart-form")) return;
		event.preventDefault();
		this.#addFromProductCard(form);
	};
	/**
	* @param {HTMLFormElement} form
	*/
	async #addFromProductCard(form) {
		const button = form.querySelector("button[type=\"submit\"]");
		if (button instanceof HTMLButtonElement) {
			button.disabled = true;
			button.setAttribute("aria-busy", "true");
			button.classList.add("vp-is-busy");
		}
		try {
			const response = await fetch(Theme.routes.cart_add_url, {
				method: "POST",
				headers: {
					Accept: "application/json",
					"X-Requested-With": "XMLHttpRequest"
				},
				body: new FormData(form)
			});
			if (!response.ok) return;
			if ((await response.json()).status) return;
			await this.openCart();
		} catch {} finally {
			if (button instanceof HTMLButtonElement) {
				button.disabled = false;
				button.removeAttribute("aria-busy");
				button.classList.remove("vp-is-busy");
			}
		}
	}
	/**
	* Swaps the list between the shopper's own recent searches and live results
	* from the Predictive Search API, which is what Search & Discovery (synonyms,
	* boosts, redirects) actually drives. Matches the prototype in only switching
	* to "Suggesties" once the query is longer than one character.
	*/
	#updateSuggestions() {
		const query = (this.searchInput?.value || "").trim();
		const filtering = query.length > 1;
		setHidden(this.searchClear, Boolean(this.searchInput?.value));
		if (this.searchListTitle) this.searchListTitle.textContent = filtering ? this.searchFilteredTitle : this.searchHistoryTitle;
		if (this.#suggestTimer) clearTimeout(this.#suggestTimer);
		this.#suggestRequest?.abort();
		if (!filtering) {
			this.#renderHistory();
			return;
		}
		setHidden(this.searchHistoryClear, false);
		this.#suggestTimer = setTimeout(() => this.#fetchSuggestions(query), 200);
	}
	/**
	* @param {string} query
	*/
	async #fetchSuggestions(query) {
		const controller = new AbortController();
		this.#suggestRequest = controller;
		const url = new URL(`${this.searchSuggestUrl}.json`, window.location.origin);
		url.searchParams.set("q", query);
		url.searchParams.set("resources[type]", "query,collection,product");
		url.searchParams.set("resources[limit]", "4");
		try {
			const response = await fetch(url, { signal: controller.signal });
			if (!response.ok) return;
			/** @type {{ resources?: { results?: Record<string, { text?: string, title?: string, url?: string }[]> } }} */
			const payload = await response.json();
			if (controller.signal.aborted) return;
			const results = payload.resources?.results || {};
			/** @type {Map<string, { text: string, url: string }>} */
			const merged = /* @__PURE__ */ new Map();
			for (const group of [
				results.queries,
				results.collections,
				results.products
			]) for (const entry of group || []) {
				const text = entry.text || entry.title;
				if (!text) continue;
				const key = text.toLowerCase();
				if (merged.has(key) || merged.size >= 6) continue;
				merged.set(key, {
					text,
					url: entry.url || ""
				});
			}
			this.#renderSuggestions([...merged.values()]);
		} catch {}
	}
	/**
	* @param {string} term
	* @returns {string}
	*/
	#searchHref(term) {
		return `${this.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
	}
	/** Zero-query state: the shopper's own recent searches. */
	#renderHistory() {
		if (!this.searchList) return;
		const history = this.#readHistory().slice(0, this.searchHistoryLimit);
		setHidden(this.searchHistoryClear, history.length > 0);
		this.searchList.innerHTML = history.map((term) => {
			const label = escapeHtml(term);
			return `<li class="vp-search-flyout__row">
            <a
              href="${escapeHtml(this.#searchHref(term))}"
              class="vp-search-flyout__suggestion type-paragraph-small"
              data-vp-search-term="${label}"
            >
              ${this.historyIconHtml}<span>${label}</span>
            </a>
            <button
              type="button"
              class="vp-search-flyout__row-remove"
              data-vp-search-history-remove="${label}"
              aria-label="${label} verwijderen"
            >${this.removeIconHtml}</button>
          </li>`;
		}).join("");
		this.#renderEmptyState(history.length === 0, this.searchHistoryEmptyText);
	}
	/**
	* Typing state: live Predictive Search results.
	* @param {{ text: string, url: string }[]} entries
	*/
	#renderSuggestions(entries) {
		if (!this.searchList) return;
		this.searchList.innerHTML = entries.map((entry) => {
			const label = escapeHtml(entry.text);
			return `<li class="vp-search-flyout__row">
            <a
              href="${escapeHtml(entry.url || this.#searchHref(entry.text))}"
              class="vp-search-flyout__suggestion type-paragraph-small"
              data-vp-search-term="${label}"
            >
              ${this.searchIconHtml}<span>${label}</span>
            </a>
          </li>`;
		}).join("");
		this.#renderEmptyState(entries.length === 0, this.searchEmptyText);
	}
	/**
	* @param {boolean} show
	* @param {string} text
	*/
	#renderEmptyState(show, text) {
		if (!show || !this.searchList) return;
		const empty = document.createElement("li");
		empty.className = "vp-search-flyout__empty type-paragraph-small";
		empty.textContent = text;
		this.searchList.append(empty);
	}
	openSearch() {
		if (!this.searchPanel) return;
		window.vpCloseBrandDropdowns?.();
		this.#closeCart({ animate: false });
		this.#active = "search";
		setHidden(this.backdrop, false);
		this.searchPanel.hidden = false;
		this.searchToggle?.setAttribute("aria-expanded", "true");
		if (isMobileViewport()) lockScroll$1(true);
		this.#updateSuggestions();
		if (gsap) gsap.fromTo(this.searchPanel, {
			autoAlpha: 0,
			y: reduced(-8)
		}, {
			autoAlpha: 1,
			y: 0,
			duration: reduced(.28),
			ease: "power2.out",
			overwrite: true
		});
		this.#bindSearchOutsideClick();
		setTimeout(() => this.searchInput?.focus(), 120);
	}
	async openCart() {
		if (!this.cartPanel) return;
		this.#forceCloseHorizonCart();
		if (this.#active === "cart" && !this.cartPanel.hidden) {
			await this.#syncCart();
			this.#forceCloseHorizonCart();
			return;
		}
		window.vpCloseBrandDropdowns?.();
		window.vpCloseFullscreenMenu?.({
			animate: false,
			restoreScroll: false
		});
		this.#closeSearch({ animate: false });
		this.#active = "cart";
		setHidden(this.backdrop, true);
		this.cartPanel.hidden = false;
		this.#setCartExpanded(true);
		lockScroll$1(true);
		if (gsap) gsap.fromTo(this.cartPanel, {
			autoAlpha: 0,
			x: reduced(24)
		}, {
			autoAlpha: 1,
			x: 0,
			duration: reduced(.32),
			ease: "power3.out",
			overwrite: true
		});
		await this.#syncCart();
		this.#forceCloseHorizonCart();
	}
	/** @param {{ animate?: boolean }} [options] */
	#closeSearch({ animate = true } = {}) {
		if (!this.searchPanel || this.searchPanel.hidden) return;
		this.#unbindSearchOutsideClick();
		this.searchToggle?.setAttribute("aria-expanded", "false");
		lockScroll$1(false);
		if (gsap && animate) {
			gsap.to(this.searchPanel, {
				autoAlpha: 0,
				y: reduced(-8),
				duration: reduced(.2),
				ease: "power2.in",
				overwrite: true,
				onComplete: () => setHidden(this.searchPanel, false)
			});
			return;
		}
		gsap?.set(this.searchPanel, {
			autoAlpha: 0,
			y: 0
		});
		setHidden(this.searchPanel, false);
	}
	/** @param {boolean} expanded */
	#setCartExpanded(expanded) {
		for (const toggle of this.cartToggles) toggle.setAttribute("aria-expanded", String(expanded));
	}
	/** @param {{ animate?: boolean }} [options] */
	#closeCart({ animate = true } = {}) {
		if (!this.cartPanel || this.cartPanel.hidden) return;
		this.#setCartExpanded(false);
		lockScroll$1(false);
		if (gsap && animate) {
			gsap.to(this.cartPanel, {
				autoAlpha: 0,
				x: reduced(24),
				duration: reduced(.24),
				ease: "power3.in",
				overwrite: true,
				onComplete: () => setHidden(this.cartPanel, false)
			});
			return;
		}
		gsap?.set(this.cartPanel, {
			autoAlpha: 0,
			x: 0
		});
		setHidden(this.cartPanel, false);
	}
	closeAll() {
		this.#unbindSearchOutsideClick();
		this.#closeSearch();
		this.#closeCart();
		setHidden(this.backdrop, false);
		this.#active = null;
	}
	/**
	* Pulls the authoritative cart so the drawer never shows stale server-rendered
	* lines after an add-to-cart elsewhere on the page.
	*/
	async #syncCart() {
		try {
			const response = await fetch(`${window.Theme?.routes?.cart_url || "/cart"}.js`, { headers: { Accept: "application/json" } });
			if (!response.ok) return;
			let cart = await response.json();
			cart = await this.#ensureGwp(cart) || cart;
			await this.#hydrateCompareAtPrices(cart);
			this.#renderCart(cart);
		} catch {}
	}
	/**
	* Reconcile the free-gift line without opening the flyout (page load / checkout path).
	*/
	async #syncGwpSilently() {
		try {
			const response = await fetch(`${window.Theme?.routes?.cart_url || "/cart"}.js`, { headers: { Accept: "application/json" } });
			if (!response.ok) return;
			const cart = await response.json();
			const beforeKeys = (cart.items || []).map((line) => line.key).join("|");
			const next = await this.#ensureGwp(cart);
			if (!next) return;
			this.#updateBadge(next.item_count);
			if (beforeKeys !== (next.items || []).map((line) => line.key).join("|") && document.body.classList.contains("template-cart")) window.location.reload();
		} catch {}
	}
	/**
	* @returns {{ enabled: boolean, threshold: number, variantId: number }}
	*/
	#gwpConfig() {
		return {
			enabled: this.cartPanel?.dataset.vpCartGiftEnabled !== "false",
			threshold: Number.parseInt(this.cartPanel?.dataset.vpCartGiftThreshold || "15000", 10) || 0,
			variantId: Number.parseInt(this.cartPanel?.dataset.vpCartGiftVariantId || "0", 10) || 0
		};
	}
	/**
	* Adds / removes / clamps the configured €0 GWP variant based on cart subtotal.
	* @param {{ items: any[], item_count?: number, items_subtotal_price?: number, currency?: string }} cart
	* @returns {Promise<object | null>} Updated cart when a mutation ran; otherwise null.
	*/
	async #ensureGwp(cart) {
		if (this.#gwpSyncing || !this.cartPanel) return null;
		this.#gwpSyncing = true;
		let current = cart;
		let mutated = false;
		try {
			const { enabled, threshold, variantId } = this.#gwpConfig();
			for (let attempt = 0; attempt < 3; attempt += 1) {
				const action = resolveGwpAction({
					items: current.items || [],
					giftEnabled: enabled,
					giftThreshold: threshold,
					giftVariantId: variantId
				});
				if (action.type === "noop") break;
				let applied = false;
				if (action.type === "add") {
					const addUrl = window.Theme?.routes?.cart_add_url || "/cart/add";
					const response = await fetch(addUrl, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json"
						},
						body: JSON.stringify({
							id: action.variantId,
							quantity: 1,
							properties: { [GWP_PROPERTY_KEY]: GWP_PROPERTY_VALUE }
						})
					});
					if (!response.ok) break;
					if ((await response.json())?.status) break;
					applied = true;
				} else if (action.type === "remove") {
					for (const key of action.keys) {
						const changeUrl = window.Theme?.routes?.cart_change_url;
						if (!(await fetch(changeUrl ? `${changeUrl}.js` : "/cart/change.js", {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Accept: "application/json"
							},
							body: JSON.stringify({
								id: key,
								quantity: 0
							})
						})).ok) {
							applied = false;
							break;
						}
						applied = true;
					}
					if (!applied) break;
				} else if (action.type === "set-qty") {
					const changeUrl = window.Theme?.routes?.cart_change_url;
					if (!(await fetch(changeUrl ? `${changeUrl}.js` : "/cart/change.js", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json"
						},
						body: JSON.stringify({
							id: action.key,
							quantity: action.quantity
						})
					})).ok) break;
					applied = true;
				}
				if (!applied) break;
				mutated = true;
				const refresh = await fetch(`${window.Theme?.routes?.cart_url || "/cart"}.js`, { headers: { Accept: "application/json" } });
				if (!refresh.ok) break;
				current = await refresh.json();
			}
			return mutated ? current : null;
		} catch {
			return mutated ? current : null;
		} finally {
			this.#gwpSyncing = false;
		}
	}
	/**
	* @param {string} key
	* @param {number} quantity
	* @param {HTMLElement} [sourceEl]
	*/
	async #changeLine(key, quantity, sourceEl) {
		this.#cartRequest?.abort();
		const cartRequest = new AbortController();
		this.#cartRequest = cartRequest;
		this.cartPanel?.setAttribute("aria-busy", "true");
		const item = sourceEl?.closest("[data-vp-cart-item]") ?? this.cartPanel?.querySelector(`[data-vp-cart-item][data-vp-cart-key="${CSS.escape(key)}"]`);
		if (item instanceof HTMLElement) item.classList.add("is-busy");
		if (sourceEl instanceof HTMLElement) {
			sourceEl.setAttribute("aria-busy", "true");
			if (sourceEl instanceof HTMLButtonElement) sourceEl.disabled = true;
		}
		try {
			const changeUrl = window.Theme?.routes?.cart_change_url;
			const response = await fetch(changeUrl ? `${changeUrl}.js` : "/cart/change.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json"
				},
				body: JSON.stringify({
					id: key,
					quantity
				}),
				signal: cartRequest.signal
			});
			if (!response.ok) return;
			let cart = await response.json();
			await this.#hydrateCompareAtPrices(cart, cartRequest.signal);
			if (cartRequest.signal.aborted) return;
			cart = await this.#ensureGwp(cart) || cart;
			if (cartRequest.signal.aborted) return;
			await this.#hydrateCompareAtPrices(cart, cartRequest.signal);
			if (cartRequest.signal.aborted) return;
			this.#renderCart(cart);
		} catch {} finally {
			this.cartPanel?.removeAttribute("aria-busy");
			if (item instanceof HTMLElement) item.classList.remove("is-busy");
			if (sourceEl instanceof HTMLElement) {
				sourceEl.removeAttribute("aria-busy");
				if (sourceEl instanceof HTMLButtonElement) sourceEl.disabled = false;
			}
		}
	}
	/**
	* Shopify's Ajax cart omits a variant's catalog compare-at price. Enrich each
	* line from the product JSON so sale pricing survives client-side cart renders.
	* @param {{ items: any[] }} cart
	* @param {AbortSignal} [signal]
	*/
	async #hydrateCompareAtPrices(cart, signal) {
		await Promise.all(cart.items.map(async (line) => {
			const variantId = Number(line.variant_id) || 0;
			if (!variantId) return;
			if (this.#variantCompareAtPrices.has(variantId)) {
				line.compare_at_price = this.#variantCompareAtPrices.get(variantId) || 0;
				return;
			}
			try {
				const productPath = new URL(line.url, window.location.origin).pathname;
				const response = await fetch(`${productPath}.js`, {
					headers: { Accept: "application/json" },
					signal
				});
				if (!response.ok) return;
				const variant = (await response.json()).variants?.find((candidate) => Number(candidate.id) === variantId);
				const compareAtPrice = Number(variant?.compare_at_price) || 0;
				this.#variantCompareAtPrices.set(variantId, compareAtPrice);
				line.compare_at_price = compareAtPrice;
			} catch {}
		}));
	}
	/**
	* Thuiswinkel terms checkbox must stay checked to allow checkout.
	* @returns {boolean}
	*/
	#termsAccepted() {
		const terms = this.cartPanel?.querySelector("[data-vp-cart-terms]");
		return !(terms instanceof HTMLInputElement) || terms.checked;
	}
	/**
	* Disable checkout when the cart is empty or terms are declined.
	* @param {number} [itemCount]
	*/
	#syncCheckoutButton(itemCount) {
		const checkoutButton = this.cartPanel?.querySelector("[data-vp-cart-checkout]");
		if (!(checkoutButton instanceof HTMLButtonElement)) return;
		let count = itemCount;
		if (count == null) {
			const countText = this.cartPanel?.querySelector("[data-vp-cart-count]")?.textContent || "0";
			count = Number.parseInt(countText, 10) || 0;
		}
		checkoutButton.disabled = count === 0 || !this.#termsAccepted();
	}
	/**
	* @param {{ items: any[], item_count: number, items_subtotal_price: number, currency: string }} cart
	*/
	#renderCart(cart) {
		if (!this.cartPanel) return;
		const money = new Intl.NumberFormat("nl-NL", {
			style: "currency",
			currency: cart.currency || "EUR"
		});
		/** @param {number} cents */
		const format = (cents) => money.format(cents / 100);
		const threshold = Number.parseInt(this.cartPanel.dataset.vpFreeShippingThreshold || "6000", 10);
		const shippingCost = Number.parseInt(this.cartPanel.dataset.vpShippingCost || "500", 10);
		const itemLabelSingular = this.cartPanel.dataset.vpCartItemSingular || "artikel";
		const itemLabelPlural = this.cartPanel.dataset.vpCartItemPlural || "artikelen";
		const shippingSuccess = this.cartPanel.dataset.vpCartShippingSuccess || "Gratis bezorging van toepassing!";
		const shippingRemainingTemplate = this.cartPanel.dataset.vpCartShippingRemainingTemplate || "Nog [amount] voor gratis bezorging";
		const giftEnabled = this.cartPanel.dataset.vpCartGiftEnabled !== "false";
		const giftThreshold = Number.parseInt(this.cartPanel.dataset.vpCartGiftThreshold || "15000", 10);
		const giftVariantId = Number.parseInt(this.cartPanel.dataset.vpCartGiftVariantId || "0", 10) || 0;
		const giftSuccess = this.cartPanel.dataset.vpCartGiftSuccess || "Gratis sleutelhanger van toepassing!";
		const giftRemainingTemplate = this.cartPanel.dataset.vpCartGiftRemainingTemplate || "Nog [amount] voor een gratis sleutelhanger";
		const giftPriceLabel = this.cartPanel.dataset.vpCartGiftPriceLabel || "Gratis";
		const emptyTitle = this.cartPanel.dataset.vpCartEmptyTitle || "Uw winkelmand is leeg";
		const emptyText = this.cartPanel.dataset.vpCartEmptyText || "Voeg producten toe om verder te gaan.";
		const emptyCta = this.cartPanel.dataset.vpCartEmptyCta || "Verder winkelen";
		const qtyDecreaseAria = this.cartPanel.dataset.vpCartQtyDecreaseAria || "Minder";
		const qtyIncreaseAria = this.cartPanel.dataset.vpCartQtyIncreaseAria || "Meer";
		const shippingFreeLabel = this.cartPanel.dataset.vpCartShippingFreeLabel || "Gratis";
		const subtotal = qualifyingSubtotal(cart.items || [], giftVariantId);
		const qualifies = subtotal >= threshold;
		const progress = resolveCartProgressStage({
			subtotal,
			shippingThreshold: threshold,
			giftEnabled,
			giftThreshold
		});
		const count = this.cartPanel.querySelector("[data-vp-cart-count]");
		if (count) count.textContent = `${cart.item_count} ${cart.item_count === 1 ? itemLabelSingular : itemLabelPlural}`;
		const shipping = this.cartPanel.querySelector("[data-vp-cart-shipping]");
		if (shipping) {
			if (progress.kind === "shipping-met" || progress.kind === "gift-met") shipping.innerHTML = `<p class="vp-cart-flyout__shipping-met">
             <svg class="vp-icon vp-cart-flyout__shipping-check" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
               <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
             </svg>
             ${progress.kind === "gift-met" ? giftSuccess : shippingSuccess}
           </p>`;
			else shipping.innerHTML = `<div class="vp-cart-flyout__shipping-row">
             <span class="vp-cart-flyout__shipping-text">${(progress.kind === "gift-remaining" ? giftRemainingTemplate : shippingRemainingTemplate).replace("[amount]", `<strong>${format(progress.remaining)}</strong>`)}</span>
             <span class="vp-cart-flyout__shipping-goal">${format(progress.goal)}</span>
           </div>
           <div class="vp-cart-flyout__shipping-track">
             <div class="vp-cart-flyout__shipping-bar" style="width: ${progress.progress}%"></div>
           </div>`;
		}
		const items = this.cartPanel.querySelector("[data-vp-cart-items]");
		if (items) items.innerHTML = cart.item_count === 0 ? `<div class="vp-cart-flyout__empty">
               <svg class="vp-icon vp-cart-flyout__empty-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                 <path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7-3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm7 19H5V8h14v14z" fill="currentColor" />
               </svg>
               <p class="vp-cart-flyout__empty-title">${emptyTitle}</p>
               <p class="vp-cart-flyout__empty-text">${emptyText}</p>
               <button type="button" class="vp-cart-flyout__empty-cta" data-vp-cart-close>${emptyCta}</button>
             </div>` : cart.items.map((line) => this.#renderLine(line, format, qtyDecreaseAria, qtyIncreaseAria, giftVariantId, giftPriceLabel)).join("");
		const footer = this.cartPanel.querySelector("[data-vp-cart-footer]");
		if (footer instanceof HTMLElement) footer.hidden = cart.item_count === 0;
		this.#syncCheckoutButton(cart.item_count);
		const subtotalEl = this.cartPanel.querySelector("[data-vp-cart-subtotal]");
		if (subtotalEl) subtotalEl.textContent = format(subtotal);
		const shippingEl = this.cartPanel.querySelector("[data-vp-cart-shipping-cost]");
			if (shippingEl) {
			shippingEl.textContent = "Wordt berekend in de checkout";
			shippingEl.classList.remove("vp-cart-flyout__line-value--free");
		}
		const totalEl = this.cartPanel.querySelector("[data-vp-cart-total]");
		if (totalEl) totalEl.textContent = format(subtotal);
		this.#updateBadge(cart.item_count);
		this.#refreshUpsells(cart, format);
	}
	/**
	* @param {{ items: any[], item_count: number }} cart
	* @param {(cents: number) => string} format
	*/
	async #refreshUpsells(cart, format) {
		const root = this.cartPanel?.querySelector("[data-vp-cart-upsells]");
		const list = this.cartPanel?.querySelector("[data-vp-cart-upsells-list]");
		const title = this.cartPanel?.querySelector("[data-vp-cart-upsells-title]");
		if (!(root instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
		const enabled = this.cartPanel?.dataset.vpCartUpsellsEnabled !== "false";
		const heading = this.cartPanel?.dataset.vpCartUpsellsTitle || "Misschien ook iets voor u";
		if (title) title.textContent = heading;
		if (!enabled || cart.item_count === 0) {
			this.#upsellRequest?.abort();
			list.innerHTML = "";
			root.hidden = true;
			return;
		}
		const seedId = pickSeedProductId(cart.items.filter((line) => !isGwpLine(line, Number.parseInt(this.cartPanel?.dataset.vpCartGiftVariantId || "0", 10) || 0)));
		if (!seedId) {
			list.innerHTML = "";
			root.hidden = true;
			return;
		}
		const limit = Number.parseInt(this.cartPanel?.dataset.vpCartUpsellsLimit || "3", 10) || 3;
		const intent = this.cartPanel?.dataset.vpCartUpsellsIntent || "related";
		const excludeIds = cartProductIds(cart.items);
		const cacheKey = `${seedId}:${intent}:${limit}`;
		/** @param {any[]} products */
		const paint = (products) => {
			const selected = filterUpsellProducts(products, excludeIds, limit);
			if (!selected.length) {
				list.innerHTML = "";
				root.hidden = true;
				return;
			}
			const addLabel = this.cartPanel?.dataset.vpCartUpsellsAddLabel || "Toevoegen";
			list.innerHTML = selected.map((product) => this.#renderUpsell(product, format, addLabel)).join("");
			root.hidden = false;
		};
		const cached = this.#upsellCache.get(cacheKey);
		if (cached) {
			paint(cached);
			return;
		}
		this.#upsellRequest?.abort();
		const upsellRequest = new AbortController();
		this.#upsellRequest = upsellRequest;
		const skeletonCount = Math.min(Math.max(limit, 2), 3);
		list.innerHTML = Array.from({ length: skeletonCount }, () => this.#renderUpsellSkeleton()).join("");
		root.hidden = false;
		try {
			const url = buildRecommendationsUrl({
				baseUrl: this.cartPanel?.dataset.vpCartRecommendationsUrl || "/recommendations/products",
				productId: seedId,
				limit: Math.max(limit + excludeIds.size, limit),
				intent
			});
			const response = await fetch(url, {
				headers: { Accept: "application/json" },
				signal: upsellRequest.signal
			});
			if (!response.ok || upsellRequest.signal.aborted) return;
			/** @type {{ products?: any[] }} */
			const payload = await response.json();
			if (upsellRequest.signal.aborted) return;
			const products = Array.isArray(payload.products) ? payload.products : [];
			this.#upsellCache.set(cacheKey, products);
			paint(products);
		} catch {
			if (!upsellRequest.signal.aborted && list.querySelector(".vp-cart-flyout__upsell--skeleton")) {
				list.innerHTML = "";
				root.hidden = true;
			}
		}
	}
	/**
	* @returns {string}
	*/
	#renderUpsellSkeleton() {
		return `
      <li class="vp-cart-flyout__upsell vp-cart-flyout__upsell--skeleton" aria-hidden="true">
        <span class="vp-cart-flyout__upsell-link">
          <span class="vp-cart-flyout__upsell-thumb vp-cart-flyout__upsell-skeleton-block"></span>
          <span class="vp-cart-flyout__upsell-copy">
            <span class="vp-cart-flyout__upsell-skeleton-line vp-cart-flyout__upsell-skeleton-line--brand"></span>
            <span class="vp-cart-flyout__upsell-skeleton-line vp-cart-flyout__upsell-skeleton-line--name"></span>
            <span class="vp-cart-flyout__upsell-skeleton-line vp-cart-flyout__upsell-skeleton-line--price"></span>
          </span>
        </span>
        <span class="vp-cart-flyout__upsell-add vp-cart-flyout__upsell-skeleton-block" aria-hidden="true"></span>
      </li>
    `;
	}
	/**
	* @param {any} product
	* @param {(cents: number) => string} format
	* @param {string} addLabel
	*/
	#renderUpsell(product, format, addLabel) {
		const imageUrl = recommendationImageUrl(product);
		const image = imageUrl ? `<img class="vp-cart-flyout__upsell-thumb-img" src="${escapeHtml(imageUrl)}" alt="" width="56" height="56" loading="lazy">` : `<span class="vp-cart-flyout__upsell-thumb-fallback" aria-hidden="true"></span>`;
		const brand = product.vendor ? `<p class="vp-cart-flyout__upsell-brand">${escapeHtml(String(product.vendor))}</p>` : "";
		const priceHtml = product.compareAtPrice > product.price ? `<span class="vp-cart-flyout__upsell-price vp-cart-flyout__upsell-price--sale">${format(product.price)}</span>
         <s class="vp-cart-flyout__upsell-compare">${format(product.compareAtPrice)}</s>` : `<span class="vp-cart-flyout__upsell-price">${format(product.price)}</span>`;
		const href = product.url ? escapeHtml(String(product.url)) : "#";
		const title = escapeHtml(String(product.title || ""));
		return `
      <li class="vp-cart-flyout__upsell" role="listitem">
        <a class="vp-cart-flyout__upsell-link" href="${href}">
          <span class="vp-cart-flyout__upsell-thumb">${image}</span>
          <span class="vp-cart-flyout__upsell-copy">
            ${brand}
            <span class="vp-cart-flyout__upsell-name">${title}</span>
            <span class="vp-cart-flyout__upsell-pricing">${priceHtml}</span>
          </span>
        </a>
        <button
          type="button"
          class="vp-cart-flyout__upsell-add"
          data-vp-cart-upsell-add="${product.variantId}"
          aria-label="${escapeHtml(addLabel)}: ${title}"
        >
          +
        </button>
      </li>
    `;
	}
	/**
	* @param {number} variantId
	* @param {HTMLElement} button
	*/
	async #addUpsellVariant(variantId, button) {
		if (button instanceof HTMLButtonElement) {
			button.disabled = true;
			button.setAttribute("aria-busy", "true");
		} else button.setAttribute("aria-busy", "true");
		try {
			const response = await fetch(Theme.routes.cart_add_url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json"
				},
				body: JSON.stringify({
					id: variantId,
					quantity: 1
				})
			});
			if (!response.ok) return;
			if ((await response.json()).status) return;
			await this.#syncCart();
		} catch {} finally {
			if (button instanceof HTMLButtonElement) button.disabled = false;
			button.removeAttribute("aria-busy");
		}
	}
	/**
	* @param {any} line
	* @param {(cents: number) => string} format
	*/
	#renderLinePrice(line, format) {
		const quantity = Number(line.quantity) || 0;
		const originalPrice = Number(line.original_price) || 0;
		const finalPrice = Number(line.final_price) || 0;
		const compareAtPrice = Number(line.compare_at_price) || 0;
		const originalLinePrice = Number(line.original_line_price) || 0;
		const compareAtLinePrice = compareAtPrice * quantity;
		let onSale = false;
		let compareLinePrice = 0;
		if (originalPrice !== finalPrice) {
			onSale = true;
			compareLinePrice = compareAtPrice > originalPrice ? compareAtLinePrice : originalLinePrice;
		} else if (compareAtPrice > originalPrice) {
			onSale = true;
			compareLinePrice = compareAtLinePrice;
		}
		const saleClass = onSale ? " vp-cart-flyout__item-price-current--sale" : "";
		const compareHtml = onSale ? `<s class="vp-cart-flyout__item-price-compare">${format(compareLinePrice)}</s>` : "";
		return `
      <div class="vp-cart-flyout__item-price">
        <span class="vp-cart-flyout__item-price-current${saleClass}">${format(line.final_line_price)}</span>
        ${compareHtml}
      </div>
    `;
	}
	/**
	* @param {any} line
	* @param {(cents: number) => string} format
	* @param {string} decreaseLabel
	* @param {string} increaseLabel
	* @param {number} giftVariantId
	* @param {string} giftPriceLabel
	*/
	#renderLine(line, format, decreaseLabel, increaseLabel, giftVariantId = 0, giftPriceLabel = "Gratis") {
		const image = line.image ? `<img class="vp-cart-flyout__thumb-img" src="${line.image.replace(/(\.[^.]*)$/, "_128x$1")}" alt="" width="64" height="64" loading="lazy">` : `<svg class="vp-icon vp-cart-flyout__thumb-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
           <path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7-3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm7 19H5V8h14v14z" fill="currentColor" />
         </svg>`;
		const brand = line.vendor ? `<p class="vp-cart-flyout__item-brand">${line.vendor}</p>` : "";
		const isGift = isGwpLine(line, giftVariantId);
		const freeLabel = escapeHtml(giftPriceLabel);
		if (isGift) return `
      <div class="vp-cart-flyout__item vp-cart-flyout__item--gwp" data-vp-cart-item data-vp-cart-key="${line.key}" data-vp-cart-gwp>
        <span class="vp-cart-flyout__thumb">${image}</span>
        <div class="vp-cart-flyout__details">
          ${brand}
          <p class="vp-cart-flyout__item-name">${line.product_title}</p>
          <div class="vp-cart-flyout__item-row">
            <span class="vp-cart-flyout__gwp-badge">${freeLabel}</span>
            <div class="vp-cart-flyout__item-price">
              <span class="vp-cart-flyout__item-price-current vp-cart-flyout__item-price-current--free">${freeLabel}</span>
            </div>
          </div>
        </div>
      </div>
    `;
		return `
      <div class="vp-cart-flyout__item" data-vp-cart-item data-vp-cart-key="${line.key}">
        <span class="vp-cart-flyout__thumb">${image}</span>
        <div class="vp-cart-flyout__details">
          ${brand}
          <p class="vp-cart-flyout__item-name">${line.product_title}</p>
          <div class="vp-cart-flyout__item-row">
            <div class="vp-cart-flyout__qty">
              <button type="button" class="vp-cart-flyout__qty-btn" data-vp-cart-qty="-1" aria-label="${decreaseLabel}">&minus;</button>
              <span class="vp-cart-flyout__qty-value" data-vp-cart-qty-value>${line.quantity}</span>
              <button type="button" class="vp-cart-flyout__qty-btn" data-vp-cart-qty="1" aria-label="${increaseLabel}">+</button>
            </div>
            ${this.#renderLinePrice(line, format)}
          </div>
        </div>
        <button type="button" class="vp-cart-flyout__remove" data-vp-cart-remove aria-label="${line.product_title} verwijderen">
          <svg class="vp-icon vp-cart-flyout__remove-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
          </svg>
        </button>
      </div>
    `;
	}
	/** @param {number} count */
	#updateBadge(count) {
		document.querySelectorAll("[data-vp-cart-badge]").forEach((badge) => {
			if (!(badge instanceof HTMLElement)) return;
			badge.textContent = String(count);
			badge.hidden = count === 0;
		});
	}
};
onDocumentReady(async () => {
	const flyouts = new HeaderFlyouts();
	gsap = await ensureGsap();
	window.vpHeaderFlyouts = flyouts;
	const drawer = document.querySelector("theme-drawer#cart-drawer");
	if (drawer instanceof HTMLElement) {
		const dialog = drawer.querySelector("dialog");
		drawer.removeAttribute("open");
		if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
		document.querySelector(".page-wrapper")?.classList.remove("page-wrapper--drawer-open");
	}
});
//#endregion
